/**
 * El resultado de una campaña contra su grupo de control (`/activa/segmentaciones/:id/resultado`, fase 5, diseño
 * `Resultado.dc.html`). Del equipo. Dos lecturas de los mismos números (ARQUITECTURA §7):
 * - **Para el cliente**: la frase, los números y qué hacer en la próxima. Las arma el backend con las mismas palabras
 *   que el PDF que se le manda («Reporte para … (PDF)»), y en su orden: primero «Gracias a la campaña» (contra el
 *   control), después lo que facturaron los que la recibieron (con los que habrían comprado igual), los descuentos, lo
 *   de más menos los descuentos y, si salió por el oficial, los mensajes en dólares (al lado, no restados).
 * - **Detalle**: la tabla por grupo, «de cada 1.000» recibieron contra el control, si es firme, el cupón, la plata y los
 *   avisos de cada grupo.
 */

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Download, Layers, Loader2 } from "lucide-react";
import { Aviso, EncabezadoPagina, EstadoVacio, Pagina } from "../../../components/activa/Pagina";
import { num } from "../../campana-nueva/utils/format";
import { usePedido } from "../../emails/hooks/usePedido";
import { rutaMensajes } from "../../mensajes/logic/mensajes";
import { segmentacionesApi } from "../../segmentaciones/api";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { R_SEGMENTACIONES, rutaGrupos } from "../../segmentaciones/logic/segmentar";
import type { Segmentacion } from "../../segmentaciones/types";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { leerError, textoError } from "../../wa-oficial/logic/respuesta";
import { enviosApi } from "../api";
import { RecorridoDelMensaje } from "../components/RecorridoDelMensaje";
import { anchos, avisosDeLaCampana, avisosDelGrupo, estadoEnPalabras, filaDelGrupo, pesos, textoCupon, textoDeLasFechas, textoFirmeza, decimal, type TonoEstado } from "../logic/resultado";
import type { Resultado, ResultadoDelGrupo } from "../types";

const RAMPA = ["#2B2179", "#4735BE", "#695EDE", "#9A91EA", "#C3BDF3"];
const TONO_ESTADO: Record<TonoEstado, string> = {
  violeta: "bg-violet-50 text-violet-700",
  verde: "bg-emerald-50 text-emerald-700",
  ambar: "bg-amber-50 text-amber-800",
  gris: "bg-slate-100 text-slate-600",
};
const TARJETA = "rounded-2xl border border-slate-200 bg-white";
const colorDe = (i: number) => RAMPA[Math.min(i, RAMPA.length - 1)];

function Punto({ color }: { color: string }) {
  return <span className="inline-block h-3 w-3 flex-none rounded-full" style={{ background: color }} aria-hidden="true" />;
}

function ParaElCliente({ r }: { r: Resultado }) {
  const l = r.lectura;
  return (
    <div className="space-y-4" data-testid="para-el-cliente">
      <section className="flex flex-col gap-2 rounded-2xl bg-slate-950 p-6 text-white sm:p-7">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-300">
          <span aria-hidden="true">● </span>La respuesta
        </p>
        <h2 className="text-2xl font-bold sm:text-3xl">{l.titular}</h2>
        <p className="text-base text-slate-200">{l.bajada}</p>
      </section>
      {/* de a tres: lo que trajo, lo bruto y los descuentos arriba; lo de más menos los descuentos y los mensajes abajo */}
      <div className="grid gap-3 sm:grid-cols-3" data-testid="tarjetas">
        {l.tarjetas.map((t) => (
          <div key={t.titulo} className={`${TARJETA} flex flex-col gap-1 p-5`}>
            <span className="text-sm text-slate-500">{t.titulo}</span>
            <span className="text-3xl font-bold text-slate-900">{t.valor}</span>
            <span className="text-sm text-slate-500">{t.detalle}</span>
          </div>
        ))}
      </div>
      {l.proxima.length > 0 && (
        <section aria-labelledby="resultado-proxima" className={`${TARJETA} p-5`}>
          <h2 id="resultado-proxima" className="mb-1 text-lg font-semibold text-slate-900">
            Qué hacemos en la próxima
          </h2>
          <ul>
            {l.proxima.map((p) => (
              <li key={p.grupos} className="flex items-start gap-3 border-t border-slate-100 py-3 text-base first:border-t-0">
                <ChevronRight size={20} className="mt-0.5 flex-none text-violet-700" aria-hidden="true" />
                <span>
                  <strong>{p.grupos}:</strong> {p.texto}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function TablaPorGrupo({ r }: { r: Resultado }) {
  const filas = [...r.grupos.map((g, i) => ({ id: g.id, nombre: g.nombre, como: `${g.canal === "email" ? "Email" : "WhatsApp"} · ${g.oferta > 0 ? `${num(g.oferta)}%` : "sin descuento"}`, color: colorDe(i), ...filaDelGrupo(g), apagado: g.recibieron.personas === 0 }))];
  for (const [k, g] of r.gruposQueNoVan.entries()) filas.push({ id: g.id, nombre: g.nombre, como: "No va en esta campaña", color: colorDe(r.grupos.length + k), envio: "No salió", tasas: "—", deMas: "—", firme: "—", apagado: true });
  return (
    <section aria-labelledby="resultado-por-grupo" className={`${TARJETA} p-5`}>
      <h2 id="resultado-por-grupo" className="mb-3 text-lg font-semibold text-slate-900">
        Por grupo
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="text-xs text-slate-500">
            <tr>
              <th className="pb-2 font-medium">Grupo</th>
              <th className="pb-2 font-medium">Envío</th>
              <th className="pb-2 font-medium">Compraron de cada 1.000: recibieron vs. no</th>
              <th className="pb-2 font-medium">Compradores de más</th>
              <th className="pb-2 font-medium">¿Es firme?</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.id} data-testid={`fila-${f.id}`} className={`border-t border-slate-100 ${f.apagado ? "text-slate-500" : "text-slate-900"}`}>
                <td className="py-3 pr-3">
                  <span className="flex items-center gap-2.5 font-semibold">
                    <Punto color={f.color} />
                    <span className="flex flex-col">
                      <span>{f.nombre}</span>
                      <span className="text-xs font-normal text-slate-500">{f.como}</span>
                    </span>
                  </span>
                </td>
                <td className="py-3 pr-3">{f.envio}</td>
                <td className="py-3 pr-3">{f.tasas}</td>
                <td className="py-3 pr-3 font-semibold">{f.deMas}</td>
                <td className="py-3">{f.firme}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const TONO_AVISO = { info: "info", atencion: "atencion", error: "error" } as const;

function DetalleDelGrupo({ g }: { g: ResultadoDelGrupo }) {
  const w = anchos(g);
  const cupon = textoCupon(g);
  return (
    <div className="space-y-5" data-testid={`detalle-${g.id}`}>
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-slate-900">De cada 1.000 personas, compraron</h3>
        <div className="space-y-1.5">
          <div className="flex justify-between gap-3 text-sm">
            <span>Recibieron · {num(g.recibieron.personas)}</span>
            <strong>{decimal(g.recibieron.porMil)}</strong>
          </div>
          <div className="h-3.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-violet-600" style={{ width: w.recibieron }} />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between gap-3 text-sm">
            <span>No recibieron (control) · {num(g.control.personas)}</span>
            <strong>{decimal(g.control.porMil)}</strong>
          </div>
          <div className="h-3.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-slate-500" style={{ width: w.control }} />
          </div>
        </div>
        <p className="text-sm text-slate-700" data-testid="firmeza">
          {textoFirmeza(g)}
        </p>
        {g.control.conElSi && <p className="text-xs text-slate-500">El control es el que tiene el sí, como quienes recibieron: el WhatsApp oficial sólo les escribe a ellos.</p>}
      </div>
      {g.recorrido && <RecorridoDelMensaje recorrido={g.recorrido} canal={g.canal} titulo={g.canal === "email" ? "El recorrido del mail" : "El recorrido del WhatsApp"} testId={`recorrido-${g.id}`} />}
      {cupon && <p className="rounded-xl bg-violet-50 px-4 py-3 text-sm text-violet-900">{cupon}</p>}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-4">
          <span className="text-sm text-slate-500">Facturaron los que recibieron</span>
          <span className="text-xl font-bold">{pesos(g.recibieron.facturacion)}</span>
        </div>
        <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-4">
          <span className="text-sm text-slate-500">Descuento usado (estimado)</span>
          <span className="text-xl font-bold">{pesos(g.recibieron.descuentoEstimado)}</span>
        </div>
        <div className="flex flex-col gap-1 rounded-xl bg-slate-50 p-4">
          <span className="text-sm text-slate-500">Vendiste de más (estimado)</span>
          <span className="text-xl font-bold">{g.medible ? pesos(g.vendisteDeMas) : "—"}</span>
          <span className="text-xs text-slate-500">{g.medible ? (g.firme ? "Firme" : "Todavía no es firme") : "Sin control"}</span>
        </div>
      </div>
      {avisosDelGrupo(g).map((a) => (
        <Aviso key={a.texto} tono={TONO_AVISO[a.tono]}>
          {a.texto}
        </Aviso>
      ))}
    </div>
  );
}

function Detalle({ r }: { r: Resultado }) {
  const [elegido, setElegido] = useState<string | null>(null);
  const conEnvio = r.grupos.filter((g) => g.recibieron.personas > 0);
  const g = conEnvio.find((x) => x.id === elegido) ?? conEnvio[0];
  return (
    <div className="space-y-4" data-testid="detalle">
      <TablaPorGrupo r={r} />
      {g && (
        <section className={`${TARJETA} space-y-5 px-5 pb-5 pt-1`}>
          <div role="tablist" aria-label="Grupo" className="flex gap-6 overflow-x-auto border-b border-slate-100">
            {conEnvio.map((x) => {
              const i = r.grupos.indexOf(x);
              const on = x.id === g.id;
              return (
                <button
                  key={x.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setElegido(x.id)}
                  className={`inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap border-b-2 px-1 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${on ? "border-violet-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                >
                  <Punto color={colorDe(i)} />
                  {x.nombre}
                </button>
              );
            })}
          </div>
          <DetalleDelGrupo g={g} />
        </section>
      )}
    </div>
  );
}

export default function ResultadoPage() {
  const { id = "" } = useParams();
  const pedido = usePedido<Resultado>(() => enviosApi.resultado(id), `resultado-${id}`);
  const campana = usePedido<Segmentacion>(() => segmentacionesApi.ver(id), `segmentacion-${id}`);
  const [vista, setVista] = useState<"cliente" | "detalle">("cliente");
  const [bajando, setBajando] = useState(false);
  const [errorPdf, setErrorPdf] = useState("");
  const r = pedido.dato;
  const marca = campana.dato?.reporte?.marca?.trim() || "el cliente";

  const bajarPdf = async () => {
    if (!r) return;
    setBajando(true);
    setErrorPdf("");
    try {
      await enviosApi.pdfDelResultado(r.campana);
    } catch (e) {
      setErrorPdf(textoError(leerError(e)));
    } finally {
      setBajando(false);
    }
  };

  const volver = (
    <Link to={R_SEGMENTACIONES} className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-violet-700 hover:text-violet-900">
      <ArrowLeft size={16} aria-hidden="true" />
      Campañas
    </Link>
  );

  if (!r) {
    return (
      <Pagina>
        {volver}
        {pedido.code === "SIN_PREPARAR" ? (
          <EstadoVacio
            icono={Layers}
            titulo="Esta campaña todavía no preparó el envío."
            testId="sin-preparar"
            accion={
              <Link to={rutaMensajes(id)} className={btnPrimario}>
                Ir a Mensajes
              </Link>
            }
          >
            El resultado se mide desde que sale el primer mensaje: preparalo al pie de Mensajes.
          </EstadoVacio>
        ) : pedido.error && !pedido.cargando ? (
          <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />
        ) : (
          <Esqueleto alto="h-96" etiqueta="Cargando el resultado" />
        )}
      </Pagina>
    );
  }

  const estado = estadoEnPalabras(r.estado);
  return (
    <Pagina>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {volver}
        <div className="flex flex-wrap gap-2">
          <Link to={rutaGrupos(id)} className={btnSecundario}>
            Grupos de esta campaña
          </Link>
          <button type="button" className={btnSecundario} disabled={bajando} onClick={() => void bajarPdf()}>
            {bajando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}
            Reporte para {marca} (PDF)
          </button>
        </div>
      </div>
      {errorPdf && <ErrorTarjeta texto={errorPdf} />}
      <EncabezadoPagina
        antetitulo="Resultado"
        titulo={r.campana.nombre}
        acciones={
          <div role="tablist" aria-label="Lectura" className="inline-flex gap-1 rounded-xl bg-slate-100 p-1">
            {(
              [
                ["cliente", "Para el cliente"],
                ["detalle", "Detalle"],
              ] as const
            ).map(([v, texto]) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={vista === v}
                onClick={() => setVista(v)}
                className={`min-h-[40px] rounded-lg px-4 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${vista === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              >
                {texto}
              </button>
            ))}
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-2.5 text-sm text-slate-500">
          <span data-testid="fechas">{textoDeLasFechas(r.estado)}</span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONO_ESTADO[estado.tono]}`} data-testid="estado">
            {estado.texto}
          </span>
        </div>
      </EncabezadoPagina>
      {avisosDeLaCampana(r).map((a) => (
        <Aviso key={a.texto} tono={TONO_AVISO[a.tono]}>
          {a.texto}
        </Aviso>
      ))}
      {vista === "cliente" ? <ParaElCliente r={r} /> : <Detalle r={r} />}
    </Pagina>
  );
}
